import api from '@/core/api/client';

export type CompanyDeletionMode = 'PRESERVE_DATA' | 'DELETE_ALL_DATA';
export type CompanyDeletionStatus = 'PENDING' | 'PROCESSING' | 'FAILED' | 'COMPLETED';

export interface CompanyDeletionJob {
  id: string;
  companyId: string | null;
  companyName: string;
  companySlug: string;
  mode: CompanyDeletionMode;
  status: CompanyDeletionStatus;
  requestedByName: string | null;
  requestedByEmail: string | null;
  requestedByDisplayName: string | null;
  failureReason: string | null;
  attempts: number;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface CompanyDeletionJobsPage {
  data: CompanyDeletionJob[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const COMPANY_DELETION_MODE_LABELS: Record<CompanyDeletionMode, string> = {
  PRESERVE_DATA: 'Arquivar (dados preservados)',
  DELETE_ALL_DATA: 'Excluir todos os dados',
};

export const COMPANY_DELETION_STATUS_LABELS: Record<CompanyDeletionStatus, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  FAILED: 'Falha',
  COMPLETED: 'Concluído',
};

/** Texto da segunda confirmação: identifica o tipo de exclusão escolhido. */
export function companyDeletionConfirmation(mode: CompanyDeletionMode, companyName: string) {
  return mode === 'PRESERVE_DATA'
    ? {
        title: 'Arquivar empresa',
        confirmText: 'Confirmar: arquivar',
        message: `Confirma o ARQUIVAMENTO de "${companyName}"? A empresa será desativada, as assinaturas ativas serão canceladas e os dados serão mantidos — ela poderá ser restaurada depois. O arquivamento será processado em segundo plano.`,
      }
    : {
        title: 'Confirmar exclusão de todos os dados',
        confirmText: 'Confirmar: excluir todos os dados',
        message: `Confirma a EXCLUSÃO DE TODOS OS DADOS de "${companyName}"? Conversas, contatos, mensagens, usuários, canais, mídia e demais dados serão removidos de forma IRREVERSÍVEL. A exclusão será processada em segundo plano e poderá levar alguns minutos.`,
      };
}

export const companyDeletionsService = {
  async request(companyId: string, mode: CompanyDeletionMode): Promise<CompanyDeletionJob> {
    const response = await api.post(`/companies/${companyId}/deletion-jobs`, { mode });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Não foi possível solicitar a exclusão');
    }
    return response.data.data;
  },

  async list(params: {
    page: number;
    limit: number;
    search?: string;
    status?: CompanyDeletionStatus;
  }): Promise<CompanyDeletionJobsPage> {
    const search = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
    if (params.search?.trim()) search.set('search', params.search.trim());
    if (params.status) search.set('status', params.status);
    const response = await api.get<{ success: boolean } & CompanyDeletionJobsPage>(
      `/companies/deletion-jobs?${search.toString()}`,
    );
    if (!response.data.success) throw new Error('Erro ao buscar exclusões');
    return { data: response.data.data, pagination: response.data.pagination };
  },
};
